<template>
    <v-dialog
        v-model="touched"
        max-width="480"
        persistent
        scrollable
    >
        <template #activator="{ on, attrs }">
            <slot v-bind="{ on, attrs }"></slot>
        </template>

        <v-card>
            <v-card-title class="customized-primary white--text">{{ t('user_card_title_profile') }}</v-card-title>

            <v-card-text class="pb-0">
                <v-form class="mt-6">
                    <v-select
                        v-model="value.level"
                        :items="levels()"
                        type="text"
                        :label="t('user_label_level')"
                        required
                        outlined
                        dense
                        append-icon="mdi-chevron-down"
                        :error-messages="form.errors.get('level')"
                    ></v-select>
                    <v-select
                        v-model="value.strokeId"
                        :items="primaryStrokes"
                        item-text="name"
                        item-value="id"
                        :label="t('user_label_fav_stroke')"
                        required
                        outlined
                        append-icon="mdi-chevron-down"
                        dense
                        :error-messages="form.errors.get('stroke_id')"
                    ></v-select>
                    <div class="form-group">
                        <span class="form-group-title">{{ t('user_label_locations') }}</span>
                        <v-checkbox
                            v-for="(location, index) in locations()"
                            v-model="value.swimmingLocations"
                            :key="'location_' + location.value"
                            :value="location.value"
                            :label="location.text"
                            :hide-details="index !== locations().length - 1"
                            multiple
                            dense
                            :error-messages="form.errors.get('swimming_locations')"
                        />
                    </div>
                    <div class="form-group">
                        <span class="form-group-title">{{ t('user_label_goals') }}</span>
                        <v-checkbox
                            v-for="(goal, index) in goals()"
                            :key="'goal_' + goal.id"
                            v-model="value.goals"
                            :value="goal.id"
                            :label="goal.name"
                            :hide-details="index !== goals.length - 1"
                            dense
                            :error-messages="form.errors.get('goals')"
                        />
                    </div>
                    <div class="form-group pt-4 pb-3">
                        <span class="form-group-title">{{ t('user_label_strokes') }}</span>
                        <v-checkbox
                            v-for="(stroke, index) in primaryStrokes"
                            :key="'stroke_' + stroke.id"
                            v-model="value.strokes"
                            :value="stroke.id"
                            :label="stroke.name"
                            :hide-details="index !== strokes.length - 1"
                            dense
                            :error-messages="form.errors.get('strokes')"
                        />
                    </div>
                    <v-select
                        v-model="value.distanceUnit"
                        :items="distanceUnits()"
                        type="text"
                        :label="t('user_label_distance_unit')"
                        required
                        outlined
                        dense
                        append-icon="mdi-chevron-down"
                        :error-messages="form.errors.get('distance_unit')"
                    ></v-select>
                    <v-text-field
                        v-model="value.swimmingGoalMeters"
                        type="number"
                        :label="t('user_label_yearly_goal')"
                        required
                        outlined
                        dense
                        :error-messages="form.errors.get('swimming_goal_meters')"
                    ></v-text-field>
                    <v-text-field
                        v-model="value.trainingCountWeek"
                        type="number"
                        :label="t('user_label_weekly_sessions')"
                        required
                        outlined
                        dense
                        :error-messages="form.errors.get('training_count_week')"
                    ></v-text-field>
                    <v-select
                        v-model="value.paceValue"
                        :items="paceValues().map(item => ({ ...item, value: Number(item.value) }))"
                        type="text"
                        :label="t('user_label_pace_value')"
                        required
                        outlined
                        dense
                        append-icon="mdi-chevron-down"
                        :error-messages="form.errors.get('pace_value')"
                    ></v-select>
                    <v-text-field
                        v-model="value.pace"
                        :label="t('user_label_pace')"
                        required
                        outlined
                        dense
                        v-maska="'##:##:##.###'"
                        :error-messages="form.errors.get('pace')"
                    ></v-text-field>
                    <v-text-field
                        v-model="value.cssValue"
                        :label="t('user_label_css_value')"
                        required
                        outlined
                        dense
                        v-maska="'##:##:##.###'"
                        :error-messages="form.errors.get('css_value')"
                    ></v-text-field>
                </v-form>
            </v-card-text>

            <v-card-actions>
                <v-btn
                    color="primary"
                    @click="submit"
                    width="45%"
                    :loading="form.busy"
                    :disabled="form.busy"
                >{{ t('general_action_update') }}</v-btn>
                <v-spacer></v-spacer>
                <v-btn
                    color="orange"
                    @click="cancel"
                    width="45%"
                    dark
                >{{ t('general_action_cancel') }}</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script lang="ts">
    import { computed, defineComponent, onMounted, reactive, toRefs } from '@vue/composition-api'
    import { useI18n } from 'vue-i18n-composable'
    import { useStore } from '@/store'
    import { useForm } from '@/core/form'
    import { useEnums, useGoals, useStrokes } from '@/core/defaults'
    import { Stroke } from '@/core/models/stroke'
    import { User } from '@/core/models/user'

    const fillable = [
        'level',
        'distance_unit',
        'swimming_locations',
        'swimming_goal_meters',
        'goals',
        'strokes',
        'stroke_id',
        'training_count_week',
        'css_value',
        'pace_value',
        'pace',
    ]

    export default defineComponent({
        name: 'UserProfileModal',

        props: {
            value: {
                type: User,
                required: true
            }
        },

        /**
         * Component constructor.
         *
         * @param props
         * @param context
         */
        setup(props, context) {
            const store = useStore()

            const i18n = useI18n()

            const state = reactive({
                touched: false,
            })

            const form = reactive(useForm())

            const { strokes: getDefaultStrokes } = useStrokes()

            const primaryStrokes = computed(() => {
                return getDefaultStrokes().filter((stroke: Stroke) => stroke.type === 'primary')
            })

            const submit = () => {
                form
                    .usingSubmit(
                        store.dispatch('user/update',
                            { id: props.value.id, payload: props.value.payload(fillable) }
                        )
                    )
                    .then(() => {
                        state.touched = false
                        store.dispatch('notify', { color: 'success', message: i18n.t('general_notice_data_updated') })
                        context.emit('submit')
                    })
            }

            const cancel = () => {
                form.errors.clear()
                state.touched = false
                context.emit('cancel')
            }

            onMounted(() => {
                store.dispatch('strokeCollection/get', { sort: 'sort_order', type: 'primary' })
            })

            return {
                form,
                submit,
                cancel,
                primaryStrokes,
                ...useEnums(),
                ...useGoals(),
                ...useStrokes(),
                ...useI18n(),
                ...toRefs(state)
            }
        }
    })
</script>
